import { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Lock, LogOut, Upload, Trash2, ArrowUp, ArrowDown,
  Mail, Eye, Calendar, ExternalLink, Image as ImageIcon, RefreshCw, Globe,
} from "lucide-react";
import { api, LOGO_URL } from "@/lib/api";

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem("stylee_token") || "");
  const [tab, setTab] = useState("brands");

  useEffect(() => {
    const onUnauth = () => setToken("");
    window.addEventListener("stylee:unauth", onUnauth);
    return () => window.removeEventListener("stylee:unauth", onUnauth);
  }, []);

  if (!token) return <LoginScreen onAuth={(t) => { localStorage.setItem("stylee_token", t); setToken(t); }} />;

  return (
    <div className="min-h-screen bg-forest text-ivory" data-testid="admin-dashboard">
      <header className="border-b border-[#264738]">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={LOGO_URL} alt="" className="h-12 w-12 object-contain" />
            <div>
              <div className="font-display text-ivory tracking-[0.25em] text-base">STYLEEPIX</div>
              <div className="text-[10px] tracking-[0.4em] text-gold mt-0.5">ADMIN PANEL</div>
            </div>
          </div>
          <div className="flex items-center gap-6">
            <a href="/" className="text-[11px] uppercase tracking-[0.3em] gold-link" data-testid="admin-view-site">View Site</a>
            <button
              onClick={() => { localStorage.removeItem("stylee_token"); setToken(""); }}
              className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-ivory-muted hover:text-gold"
              data-testid="admin-logout"
            >
              <LogOut size={14} /> Sign Out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-10">
        <div className="flex gap-8 border-b border-[#264738] mb-10">
          {[
            { k: "brands", label: "Brand Logos" },
            { k: "contacts", label: "Inquiries" },
            { k: "behance", label: "Behance Sync" },
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              className={`pb-4 text-[11px] uppercase tracking-[0.35em] border-b-2 -mb-px transition-colors ${
                tab === t.k ? "border-gold text-gold" : "border-transparent text-ivory-muted hover:text-ivory"
              }`}
              data-testid={`admin-tab-${t.k}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "brands" && <BrandsManager />}
        {tab === "contacts" && <ContactsList />}
        {tab === "behance" && <BehanceSync />}
      </div>
    </div>
  );
}

function LoginScreen({ onAuth }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post("/auth/login", { email, password });
      onAuth(r.data.token);
      toast.success("Welcome back.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-forest flex items-center justify-center px-6" data-testid="admin-login">
      <motion.form
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        onSubmit={onSubmit}
        className="w-full max-w-md border border-[#264738] bg-[#162E24] p-10 md:p-12"
      >
        <div className="flex items-center gap-3 mb-2">
          <Lock size={16} className="text-gold" />
          <span className="text-[10px] uppercase tracking-[0.4em] text-gold">Restricted</span>
        </div>
        <h1 className="font-display text-3xl md:text-4xl text-ivory leading-tight">Admin Sign-In</h1>
        <p className="text-ivory-muted text-sm font-light mt-3 mb-10">Enter your credentials to continue.</p>

        <div className="space-y-7">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.4em] text-ivory-muted mb-3">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full bg-transparent border-0 border-b border-[#264738] focus:border-gold py-3 text-ivory outline-none"
              data-testid="login-email"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.4em] text-ivory-muted mb-3">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full bg-transparent border-0 border-b border-[#264738] focus:border-gold py-3 text-ivory outline-none"
              data-testid="login-password"
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="mt-10 w-full bg-gold text-forest hover:bg-gold-400 disabled:opacity-50 py-4 text-[11px] uppercase tracking-[0.35em] transition-colors"
          data-testid="login-submit"
        >
          {loading ? "Signing in…" : "Sign In"}
        </button>
      </motion.form>
    </div>
  );
}

function BrandsManager() {
  const qc = useQueryClient();
  const [uploadOpen, setUploadOpen] = useState(false);
  const [name, setName] = useState("");
  const [website, setWebsite] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const { data: brands = [], isLoading: loading } = useQuery({
    queryKey: ["brands"],
    queryFn: async () => (await api.get("/brands")).data,
  });
  const reload = () => qc.invalidateQueries({ queryKey: ["brands"] });

  const upload = async (e) => {
    e.preventDefault();
    if (!file || !name) return toast.error("Name and image required.");
    setUploading(true);
    const fd = new FormData();
    fd.append("name", name);
    if (website) fd.append("website", website);
    fd.append("file", file);
    try {
      await api.post("/brands", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Brand added.");
      setName(""); setWebsite(""); setFile(null); setUploadOpen(false);
      if (fileRef.current) fileRef.current.value = "";
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this brand from the carousel?")) return;
    try {
      await api.delete(`/brands/${id}`);
      toast.success("Removed.");
      reload();
    } catch { toast.error("Failed."); }
  };

  const move = async (idx, dir) => {
    const newOrder = [...brands];
    const j = idx + dir;
    if (j < 0 || j >= newOrder.length) return;
    [newOrder[idx], newOrder[j]] = [newOrder[j], newOrder[idx]];
    qc.setQueryData(["brands"], newOrder);
    try {
      await api.post("/brands/reorder", newOrder.map((b) => b.id));
    } catch { toast.error("Reorder failed."); reload(); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <h2 className="font-display text-3xl text-ivory">Brand Carousel</h2>
          <p className="text-ivory-muted text-sm mt-1">Logos shown on the homepage marquee.</p>
        </div>
        <button
          onClick={() => setUploadOpen(!uploadOpen)}
          className="inline-flex items-center gap-2 bg-gold text-forest hover:bg-gold-400 px-6 py-3 text-[11px] uppercase tracking-[0.3em]"
          data-testid="add-brand-toggle"
        >
          <Upload size={14} /> {uploadOpen ? "Close" : "Add Brand"}
        </button>
      </div>

      {uploadOpen && (
        <motion.form
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          onSubmit={upload}
          className="border border-[#264738] bg-[#162E24] p-6 md:p-8 mb-10 grid grid-cols-1 md:grid-cols-12 gap-6"
          data-testid="add-brand-form"
        >
          <div className="md:col-span-4">
            <label className="block text-[10px] uppercase tracking-[0.4em] text-ivory-muted mb-3">Brand Name</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="w-full bg-transparent border-0 border-b border-[#264738] focus:border-gold py-3 text-ivory outline-none"
              data-testid="brand-name-input"
            />
          </div>
          <div className="md:col-span-4">
            <label className="block text-[10px] uppercase tracking-[0.4em] text-ivory-muted mb-3">Website (optional)</label>
            <input
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              placeholder="https://"
              className="w-full bg-transparent border-0 border-b border-[#264738] focus:border-gold py-3 text-ivory outline-none"
              data-testid="brand-website-input"
            />
          </div>
          <div className="md:col-span-4">
            <label className="block text-[10px] uppercase tracking-[0.4em] text-ivory-muted mb-3">Logo (PNG/SVG)</label>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              onChange={(e) => setFile(e.target.files?.[0])}
              className="block w-full text-ivory-muted text-sm file:bg-gold/10 file:text-gold file:border-0 file:px-4 file:py-2 file:mr-3 file:text-[11px] file:uppercase file:tracking-[0.2em] file:cursor-pointer cursor-pointer"
              data-testid="brand-file-input"
            />
          </div>
          <div className="md:col-span-12">
            <button
              type="submit"
              disabled={uploading}
              className="bg-gold text-forest px-6 py-3 text-[11px] uppercase tracking-[0.3em] disabled:opacity-50"
              data-testid="brand-upload-submit"
            >
              {uploading ? "Uploading…" : "Save Brand"}
            </button>
          </div>
        </motion.form>
      )}

      {loading ? (
        <p className="text-ivory-muted text-sm">Loading…</p>
      ) : brands.length === 0 ? (
        <div className="border border-dashed border-[#264738] p-16 text-center">
          <ImageIcon className="mx-auto text-ivory-muted mb-4" size={32} />
          <p className="text-ivory-muted text-sm">No brands yet. Add your first one above.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {brands.map((b, i) => (
            <div key={b.id} className="border border-[#264738] bg-[#162E24] p-6 group" data-testid={`brand-row-${b.id}`}>
              <div className="h-28 flex items-center justify-center mb-4 bg-[#0F211A]">
                <img src={b.image_url} alt={b.name} className="max-h-full max-w-full object-contain" />
              </div>
              <div className="text-ivory font-display text-lg truncate">{b.name}</div>
              {b.website && (
                <a href={b.website} target="_blank" rel="noreferrer" className="text-ivory-muted hover:text-gold text-xs flex items-center gap-1 mt-1 truncate">
                  <Globe size={11} /> {b.website}
                </a>
              )}
              <div className="flex items-center gap-2 mt-4 pt-4 border-t border-[#264738]">
                <button onClick={() => move(i, -1)} disabled={i === 0} className="p-2 text-ivory-muted hover:text-gold disabled:opacity-30" title="Move up" data-testid={`brand-up-${b.id}`}>
                  <ArrowUp size={14} />
                </button>
                <button onClick={() => move(i, 1)} disabled={i === brands.length - 1} className="p-2 text-ivory-muted hover:text-gold disabled:opacity-30" title="Move down" data-testid={`brand-down-${b.id}`}>
                  <ArrowDown size={14} />
                </button>
                <span className="flex-1 text-xs text-ivory-muted">#{i + 1}</span>
                <button onClick={() => remove(b.id)} className="p-2 text-ivory-muted hover:text-red-400" title="Delete" data-testid={`brand-delete-${b.id}`}>
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ContactsList() {
  const qc = useQueryClient();
  const [selected, setSelected] = useState(null);

  const { data: items = [], isLoading: loading } = useQuery({
    queryKey: ["contacts"],
    queryFn: async () => (await api.get("/contact")).data,
  });
  const reload = () => qc.invalidateQueries({ queryKey: ["contacts"] });

  const markRead = async (id) => {
    try { await api.patch(`/contact/${id}/read`); reload(); } catch (e) { void e; }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this inquiry?")) return;
    try { await api.delete(`/contact/${id}`); reload(); setSelected(null); toast.success("Deleted."); } catch (e) { void e; }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
      <div className="lg:col-span-5">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="font-display text-3xl text-ivory">Inquiries</h2>
            <p className="text-ivory-muted text-sm mt-1">{items.length} total · {items.filter((c) => !c.read).length} unread</p>
          </div>
          <button onClick={reload} className="text-ivory-muted hover:text-gold" data-testid="contacts-refresh"><RefreshCw size={16} /></button>
        </div>
        {loading ? (
          <p className="text-ivory-muted text-sm">Loading…</p>
        ) : items.length === 0 ? (
          <div className="border border-dashed border-[#264738] p-12 text-center">
            <Mail className="mx-auto text-ivory-muted mb-3" size={28} />
            <p className="text-ivory-muted text-sm">No inquiries yet.</p>
          </div>
        ) : (
          <div className="border border-[#264738] divide-y divide-[#264738] max-h-[70vh] overflow-auto">
            {items.map((c) => (
              <button
                key={c.id}
                onClick={() => { setSelected(c); if (!c.read) markRead(c.id); }}
                className={`w-full text-left p-5 hover:bg-[#162E24] transition-colors ${selected?.id === c.id ? "bg-[#162E24]" : ""}`}
                data-testid={`contact-row-${c.id}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  {!c.read && <span className="w-1.5 h-1.5 bg-gold rounded-full" />}
                  <span className="text-ivory font-display text-base truncate">{c.name}</span>
                </div>
                <div className="text-ivory-muted text-xs truncate">{c.email} · {c.brand || "—"}</div>
                <div className="text-ivory-muted text-xs mt-2 line-clamp-2">{c.message}</div>
                <div className="text-ivory-muted/70 text-[10px] uppercase tracking-[0.2em] mt-2 flex items-center gap-1">
                  <Calendar size={10} />{new Date(c.created_at).toLocaleString()}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="lg:col-span-7">
        {selected ? (
          <div className="border border-[#264738] bg-[#162E24] p-8 md:p-10">
            <div className="flex items-start justify-between mb-8">
              <div>
                <h3 className="font-display text-3xl text-ivory">{selected.name}</h3>
                <a href={`mailto:${selected.email}`} className="text-gold text-sm hover:underline">{selected.email}</a>
                {selected.brand && <div className="text-ivory-muted text-sm mt-1">{selected.brand}</div>}
              </div>
              <div className="flex items-center gap-2">
                <a href={`mailto:${selected.email}?subject=Re: Your StyleePix Inquiry`} className="p-2 text-ivory-muted hover:text-gold border border-[#264738]"><Mail size={16} /></a>
                <button onClick={() => remove(selected.id)} className="p-2 text-ivory-muted hover:text-red-400 border border-[#264738]" data-testid="contact-delete"><Trash2 size={16} /></button>
              </div>
            </div>
            <div className="hairline mb-6" />
            <p className="text-ivory text-base font-light leading-relaxed whitespace-pre-wrap">{selected.message}</p>
            <div className="text-ivory-muted text-xs uppercase tracking-[0.2em] mt-10 flex items-center gap-2">
              <Calendar size={12} />{new Date(selected.created_at).toLocaleString()}
            </div>
          </div>
        ) : (
          <div className="border border-dashed border-[#264738] h-full min-h-[400px] flex items-center justify-center text-ivory-muted text-sm">
            <div className="flex flex-col items-center gap-3">
              <Eye size={28} />
              <p>Select an inquiry to view details.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


function BehanceSync() {
  const qc = useQueryClient();
  const [syncing, setSyncing] = useState(false);
  const { data: projects = [], isLoading } = useQuery({
    queryKey: ["behance-admin"],
    queryFn: async () => (await api.get("/behance")).data,
  });

  const resync = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/behance/resync");
      if (r.data.ok) {
        toast.success(`Synced ${r.data.count} projects from Behance.`);
      } else {
        toast.error("Behance returned 0 projects (likely rate-limited). The last cached snapshot stays live.");
      }
      qc.invalidateQueries({ queryKey: ["behance-admin"] });
      qc.invalidateQueries({ queryKey: ["behance"] });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Resync failed.");
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div data-testid="behance-sync-panel">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <h2 className="font-display text-3xl text-ivory">Behance Sync</h2>
          <p className="text-ivory-muted text-sm mt-1">
            Last cached snapshot: <span className="text-ivory">{projects.length}</span> projects. Auto-refreshes every 6h.
          </p>
        </div>
        <button
          onClick={resync}
          disabled={syncing}
          className="inline-flex items-center gap-2 bg-gold text-forest hover:bg-gold-400 disabled:opacity-50 px-6 py-3 text-[11px] uppercase tracking-[0.3em]"
          data-testid="behance-resync-btn"
        >
          <RefreshCw size={14} className={syncing ? "animate-spin" : ""} /> {syncing ? "Syncing…" : "Resync Now"}
        </button>
      </div>

      {isLoading ? (
        <p className="text-ivory-muted text-sm">Loading…</p>
      ) : projects.length === 0 ? (
        <div className="border border-dashed border-[#264738] p-16 text-center">
          <ExternalLink className="mx-auto text-ivory-muted mb-4" size={28} />
          <p className="text-ivory-muted text-sm mb-2">No cached projects yet.</p>
          <p className="text-ivory-muted/70 text-xs">Click &quot;Resync Now&quot; — if Behance is rate-limiting us, try again in a few minutes.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {projects.map((p) => (
            <a
              key={p.id}
              href={p.url}
              target="_blank"
              rel="noreferrer"
              className="group border border-[#264738] bg-[#162E24] overflow-hidden"
              data-testid={`behance-admin-item-${p.id}`}
            >
              <div className="aspect-[4/3] overflow-hidden">
                {p.cover_image && (
                  <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                )}
              </div>
              <div className="p-4">
                <div className="text-ivory text-sm font-display truncate">{p.title}</div>
                <div className="text-ivory-muted/70 text-[10px] uppercase tracking-[0.2em] mt-1">#{p.id}</div>
              </div>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
